import { EncounterChargeItemRow } from "@/encounters/types/encounter-charge-item-rows";
import { StagedProceduresOperation } from "@/encounters/types/procedures";

/** One edit a biller finished in the picker, waiting for Save Billing Info to write it. */
export type StagedBillerSectionChargeEdit = {
  /** The captured charge the edited row is. Also the row id the preview below matches on. */
  chargeItemId: string;
  /** The picker session: one locked procedure code and the diagnoses it ended up billed under. */
  operation: StagedProceduresOperation;
};

/**
 * Returns the rows with every staged edit applied, which is what the table renders.
 *
 * The section writes nothing until Save Billing Info, so an edited row has to be previewed the same
 * way a staged addition is. Only the fields an edit can change are replaced: the code and the units
 * are locked in the picker, so they stay as the charge recorded them.
 *
 * `editTarget` is replaced too, so re-opening Edit on a row starts from the edit in front of the
 * biller rather than from what the server last returned.
 *
 * A row with no staged edit is returned untouched, and an edit naming a row that is not there is
 * ignored. That happens when a biller edits a row and then deletes it, and it means the preview
 * never invents a row the encounter does not have.
 */
export function applyStagedChargeEdits(
  rows: EncounterChargeItemRow[],
  edits: StagedBillerSectionChargeEdit[],
): EncounterChargeItemRow[] {
  if (!edits.length) return rows;

  // Keyed by charge, latest session winning: a biller who opens the same row twice means the second
  // session, and only one of them can be written.
  const editsByChargeItemId = new Map(edits.map((edit) => [edit.chargeItemId, edit]));

  return rows.map((row) => {
    const edit = editsByChargeItemId.get(row.id);
    if (!edit) return row;

    const [procedure] = edit.operation.formValues.procedures;
    if (!procedure) return row;

    const conditions = edit.operation.formValues.conditions;

    return {
      ...row,
      modifiers: procedure.modifiers.filter((modifier) => Boolean(modifier?.trim())),
      diagnosisCodes: conditions.map(({ code }) => code),
      editTarget: { procedure, conditions },
    };
  });
}
